import {
  Badge,
  Box,
  Button,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useRef } from "react";

import api from "@/common/utils/api";
import { companyQueryKeys } from "@/company/data/companyQueryKeys";
import { featureQueryKeys } from "@/feature/data/featureQueryKeys";
import type { BackgroundOperationDTO } from "@reflag/shared/backgroundOperationAPI";

export const backgroundOperationsQueryKey = (appId?: string, envId?: string) =>
  ["apps", appId, "environments", envId, "background-operations"] as const;

export function useBackgroundOperations(appId?: string, envId?: string) {
  const queryClient = useQueryClient();
  const refreshed = useRef(new Set<string>());
  const query = useQuery({
    queryKey: backgroundOperationsQueryKey(appId, envId),
    queryFn: async () => {
      const response =
        await api.get<"/apps/:appId/envs/:envId/background-operations">(
          `/apps/${appId}/envs/${envId}/background-operations`,
        );
      return response.data;
    },
    enabled: !!appId && !!envId,
    // Also discover work started by another admin. Polling stops on unmount.
    refetchInterval: 3000,
  });

  useEffect(() => {
    const completed =
      query.data?.filter(
        (operation) =>
          operation.type === "entity-attribute-deletion" &&
          operation.status === "completed" &&
          !refreshed.current.has(operation.id),
      ) ?? [];
    if (!completed.length) return;
    completed.forEach((operation) => refreshed.current.add(operation.id));
    const keys = [
      companyQueryKeys.attributes(appId, envId).slice(0, -1),
      ...(["company", "user"] as const).map((type) =>
        featureQueryKeys
          .targetingContextAttributeKeys(appId, envId, type)
          .slice(0, -1),
      ),
    ];
    keys.forEach((queryKey) => {
      void queryClient.invalidateQueries({ queryKey });
    });
  }, [query.data, appId, envId, queryClient]);

  return query;
}

export function BackgroundOperationsSection({
  appId,
  envId,
}: {
  appId?: string;
  envId?: string;
}) {
  const operations = useBackgroundOperations(appId, envId);
  return (
    <BackgroundOperations
      operations={operations.data ?? []}
      isError={operations.isError}
      isLoading={operations.isPending}
      onRetry={() => {
        void operations.refetch();
      }}
    />
  );
}

const statusLabels = {
  queued: "Queued",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
} as const;
const statusColors = {
  queued: "gray",
  running: "blue",
  completed: "green",
  failed: "red",
} as const;

export function BackgroundOperations({
  operations,
  isError,
  isLoading,
  onRetry,
}: {
  operations: BackgroundOperationDTO[];
  isError: boolean;
  isLoading: boolean;
  onRetry: () => void;
}) {
  const cutoff = Date.now() - 24 * 60 * 60 * 1000;
  const visibleOperations = operations.filter(
    (operation) =>
      operation.status === "queued" ||
      operation.status === "running" ||
      new Date(
        operation.completedAt ?? operation.startedAt ?? operation.createdAt,
      ).getTime() >= cutoff,
  );
  if (!visibleOperations.length && !isError) return null;

  return (
    <VStack align="stretch" spacing={3} aria-label="Recent data jobs">
      {!!visibleOperations.length && (
        <Text fontWeight="semibold">Recent data jobs</Text>
      )}
      {isError && (
        <Box role="alert">
          <Text color="red.500">
            Could not refresh background operation status. Displayed status may
            be out of date.
          </Text>
          <Button size="sm" onClick={onRetry}>
            Retry status check
          </Button>
        </Box>
      )}
      {isLoading && (
        <Text fontSize="sm" color="dimmed">
          Loading background operations…
        </Text>
      )}
      {!!visibleOperations.length && (
        <TableContainer>
          <Table size="sm" aria-label="Recent data jobs">
            <Thead>
              <Tr>
                <Th>Started</Th>
                <Th>Description</Th>
                <Th>Started by</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {visibleOperations.map((operation) => (
                <Tr key={operation.id}>
                  <Td color="dimmed">
                    {operation.startedAt
                      ? dayjs(operation.startedAt).format("MMM D, HH:mm")
                      : "—"}
                  </Td>
                  <Td>{operation.title}</Td>
                  <Td>{operation.requestedByName || "—"}</Td>
                  <Td>
                    <Tooltip
                      label={
                        operation.status === "completed"
                          ? undefined
                          : operation.errorMessage ||
                            (operation.status === "running"
                              ? `${operation.processedItems.toLocaleString()} items processed in this attempt`
                              : "Waiting to run in the background")
                      }
                    >
                      <Badge
                        tabIndex={0}
                        colorScheme={statusColors[operation.status]}
                      >
                        {statusLabels[operation.status]}
                      </Badge>
                    </Tooltip>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </VStack>
  );
}
